import React, { useState, useEffect } from 'react';

// Détecte iOS
const isIOS = () => {
  return (
    /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

// Détecte si déjà installée (standalone mode)
const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const PWAInstallBanner = () => {
  const [showIOSBanner, setShowIOSBanner] = useState(false);
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Ne pas afficher si déjà installée ou déjà refusée
    const alreadyDismissed = localStorage.getItem('pwa-banner-dismissed');
    if (alreadyDismissed || isInStandaloneMode()) return;

    // iOS — pas de prompt automatique, on affiche les instructions manuelles
    if (isIOS()) {
      // Attendre 3s avant d'afficher
      const timer = setTimeout(() => setShowIOSBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android / Chrome — utiliser l'event beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowAndroidPrompt(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('pwa-banner-dismissed', 'true');
    setShowIOSBanner(false);
    setShowAndroidPrompt(false);
    setDismissed(true);
  };

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      handleDismiss();
    }
    setDeferredPrompt(null);
  };

  if (dismissed) return null;

  // ── Bannière iOS ──────────────────────────────────────────────────────────
  if (showIOSBanner) {
    return (
      <div style={styles.overlay}>
        <div style={styles.banner}>
          {/* Header */}
          <div style={styles.header}>
            <img src="/apple-touch-icon.png" alt="RE-GLOW" style={styles.appIcon} />
            <div style={styles.headerText}>
              <span style={styles.appName}>RE-GLOW</span>
              <span style={styles.appSubtitle}>Ajouter à l'écran d'accueil</span>
            </div>
            <button onClick={handleDismiss} style={styles.closeBtn} aria-label="Fermer">✕</button>
          </div>

          {/* Divider */}
          <div style={styles.divider} />

          {/* Instructions */}
          <p style={styles.instructionTitle}>Installer l'app sur votre iPhone :</p>

          <div style={styles.steps}>
            <div style={styles.step}>
              <div style={styles.stepNumber}>1</div>
              <div style={styles.stepContent}>
                <span style={styles.stepText}>Appuyez sur </span>
                <span style={styles.shareIcon}>⎙</span>
                <span style={styles.stepText}> (le bouton Partager en bas de Safari)</span>
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>2</div>
              <div style={styles.stepContent}>
                <span style={styles.stepText}>Faites défiler et appuyez sur </span>
                <span style={styles.bold}>"Sur l'écran d'accueil"</span>
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>3</div>
              <div style={styles.stepContent}>
                <span style={styles.stepText}>Appuyez sur </span>
                <span style={styles.bold}>"Ajouter"</span>
                <span style={styles.stepText}> — c'est tout !</span>
              </div>
            </div>
          </div>

          {/* Visual hint arrow pointing down to Safari toolbar */}
          <div style={styles.arrowHint}>
            <span style={styles.arrowText}>↓ Bouton Partager ici</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Bannière Android ──────────────────────────────────────────────────────
  if (showAndroidPrompt) {
    return (
      <div style={styles.androidBanner}>
        <img src="/apple-touch-icon.png" alt="RE-GLOW" style={styles.androidIcon} />
        <div style={styles.androidText}>
          <span style={styles.androidTitle}>Installer RE-GLOW</span>
          <span style={styles.androidSub}>Accès rapide depuis votre écran d'accueil</span>
        </div>
        <button onClick={handleAndroidInstall} style={styles.installBtn}>Installer</button>
        <button onClick={handleDismiss} style={styles.androidClose} aria-label="Fermer">✕</button>
      </div>
    );
  }

  return null;
};

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  // iOS modal overlay
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    padding: '0 0 0 0',
    animation: 'fadeIn 0.3s ease',
  },
  banner: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#fff',
    borderRadius: '24px 24px 0 0',
    padding: '24px 24px 40px',
    boxShadow: '0 -4px 40px rgba(0,0,0,0.15)',
    animation: 'slideUp 0.35s cubic-bezier(0.32,0.72,0,1)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  appIcon: {
    width: 52,
    height: 52,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  appName: {
    fontSize: 17,
    fontWeight: 700,
    color: '#1a1a1a',
    letterSpacing: '-0.3px',
  },
  appSubtitle: {
    fontSize: 13,
    color: '#888',
    marginTop: 2,
  },
  closeBtn: {
    background: '#f0f0f0',
    border: 'none',
    borderRadius: '50%',
    width: 28,
    height: 28,
    cursor: 'pointer',
    fontSize: 13,
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  divider: {
    height: 1,
    backgroundColor: '#f0f0f0',
    marginBottom: 16,
  },
  instructionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: 16,
    margin: '0 0 16px 0',
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },
  stepNumber: {
    minWidth: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: '#B58A5A',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stepContent: {
    fontSize: 14,
    color: '#444',
    lineHeight: 1.5,
    paddingTop: 4,
  },
  stepText: {
    color: '#444',
  },
  shareIcon: {
    fontSize: 18,
    color: '#007AFF',
    verticalAlign: 'middle',
  },
  bold: {
    fontWeight: 600,
    color: '#1a1a1a',
  },
  arrowHint: {
    marginTop: 20,
    textAlign: 'center',
  },
  arrowText: {
    fontSize: 12,
    color: '#B58A5A',
    fontWeight: 600,
    letterSpacing: '0.3px',
  },

  // Android bottom banner
  androidBanner: {
    position: 'fixed',
    bottom: 16,
    left: 16,
    right: 16,
    zIndex: 9999,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    animation: 'slideUp 0.3s ease',
  },
  androidIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
  },
  androidText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  androidTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1a1a1a',
  },
  androidSub: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  installBtn: {
    backgroundColor: '#B58A5A',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  androidClose: {
    background: 'transparent',
    border: 'none',
    color: '#999',
    fontSize: 18,
    cursor: 'pointer',
    padding: 4,
  },
};

export default PWAInstallBanner;
